/**
 * estadosObra.js — Modelo de hitos de Obras PVC (proceso confirmado 2026-07-07).
 *
 * La obra NO avanza por un pipeline manual: su progreso se DERIVA de hechos
 * (documentos adjuntos + marcas). Hitos y sus dependencias:
 *
 *   1. pedido    = OP adjunto + pedido Finnegans vinculado   (primero, sí o sí)
 *   2. despacho  = despacho adjunto (Marcela)                (requiere 1)
 *   3. entregada = marca manual de Ceci                      (requiere 2)
 *      facturada = factura adjunta (Marcela)                 (requiere 2 — puede ser antes o después de la entrega)
 *      cobrada   = cobro adjunto (Judy)                      (requiere factura)
 *      balance   = balance hecho (Judy/Andrés)               (requiere 2 — antes o después de la cobranza)
 *
 *   FINALIZADA = todos los hitos completos.
 *
 * La "fase" (para el tablero y alertas) también se deriva:
 *   CREACION → PREPARACION_R8 → EN_CARPINTERIA → ENTREGADA (admin pendiente) → FINALIZADA
 */

export const CARPINTEROS = ["JAM", "JOR", "WILL", "SAN JOSE", "GUALE"];

// Documentos del legajo de la obra
export const DOCS_OBRA = [
  { tipo: "presupuesto",    label: "Presupuesto OP",         quien: "Ceci",    hint: "Presupuesto aceptado por el cliente (define el monto de la obra)" },
  { tipo: "op",             label: "Pedido OP",              quien: "Ceci",    hint: "Requisito para vincular el pedido de Finnegans" },
  { tipo: "facturaCompra",  label: "Factura de compra (accesorios)", quien: "Marcela", hint: "La compra de los accesorios en plaza" },
  { tipo: "facturaCompraFinnegans", label: "Factura de compra de Finnegans", quien: "Marcela", hint: "La factura de los accesorios comprados por Finnegans" },
  { tipo: "recepcionCompra", label: "Recepción de la compra", quien: "Marcela", hint: "Los accesorios entran a la empresa" },
  { tipo: "despacho",       label: "Despacho R8 (perfiles)",  quien: "Marcela", hint: "Los perfiles y refuerzos salen de R8 al carpintero" },
  { tipo: "despachoGuale",  label: "Despacho Gualeguay (telas + accesorios)", quien: "Ceci", hint: "Requiere la recepción de la compra hecha" },
  { tipo: "factura",        label: "Factura de venta",       quien: "Marcela", hint: "Se puede facturar antes o después de la entrega" },
  { tipo: "cobro",          label: "Documento de cobro",     quien: "Judy",    hint: "Siempre después de la factura" },
];

function archivoDe(obra, tipo) {
  if (!obra) return null;
  const nuevo = obra.archivos?.[tipo] || null;
  if (nuevo) return nuevo;
  if (tipo === "op") return obra.archivoOP || null; // compat con obras viejas
  return null;
}

// Hitos que el admin puede marcar "cargado (pendiente de archivo)" para poder
// dar de alta obras ya avanzadas sin tener todavía el documento (obra.hitosManual).
export const HITOS_MANUALES = ["op", "vinculado", "facturaCompra", "recepcionCompra", "despacho", "despachoGuale"];

/**
 * OPs de la obra. La producción puede hacerse en ETAPAS (distintos carpinteros),
 * y entonces la obra tiene VARIOS pedidos de OP bajo un mismo presupuesto. Los
 * productos de todos se suman para armar los dos pedidos de Finnegans (R8 y GUALE).
 * Compat: obras viejas con un único `archivos.op` se devuelven como un OP.
 */
export function opsDeObra(obra) {
  const lista = Array.isArray(obra?.ops) ? obra.ops : [];
  if (lista.length > 0) return lista;
  const legacy = archivoDe(obra, "op");
  return legacy ? [{ id: "legacy", archivo: legacy, carpintero: obra?.carpintero || null }] : [];
}

/** Archivos de todos los OPs, para parsear y sumar sus ítems. */
export function archivosOPs(obra) {
  return opsDeObra(obra).map(o => o.archivo).filter(Boolean);
}

/** Estado de cada hito de la obra (true/false) */
export function hitosObra(obra) {
  const man = obra?.hitosManual || {}; // overrides de admin (pendientes de archivo)
  const presupuesto = !!archivoDe(obra, "presupuesto");
  // Obras muy viejas pueden no tener ni OP ni pedido de Finnegans: el admin las
  // marca a mano para poder avanzar (quedan señaladas como pendientes).
  const op = opsDeObra(obra).length > 0 || !!man.op;
  // El pedido puede estar vinculado desde el sistema (pedidoId), adjuntado y
  // leído a mano (pedidoAdjuntoR8), o marcado por admin si nunca existió.
  const vinculado = !!obra?.pedidoId || !!obra?.pedidoAdjuntoR8 || !!man.vinculado;
  return {
    presupuesto,
    op,
    vinculado,
    pedido: op && vinculado,
    // Compra de accesorios: los accesorios varios NO se stockean ni se despachan.
    // Alcanza con documentar la compra: comprobantes de plaza (subcolección
    // comprasPlaza) Y/O la factura de compra de Finnegans. (facturaCompra legacy compat)
    facturaCompra: (Number(obra?.comprasPlazaCount) || 0) > 0
      || !!archivoDe(obra, "facturaCompraFinnegans")
      || !!archivoDe(obra, "facturaCompra")
      || !!man.facturaCompra,
    recepcionCompra: !!archivoDe(obra, "recepcionCompra") || !!man.recepcionCompra,
    despacho: !!archivoDe(obra, "despacho") || !!man.despacho,           // R8: perfiles/refuerzos
    despachoGuale: !!archivoDe(obra, "despachoGuale") || !!man.despachoGuale, // Guale: telas + accesorios
    entregada: !!obra?.entregada,
    // Facturada = la factura de cada vía cubre lo COBRADO por ese método.
    // Finnegans (blanco) = cobrado NO efectivo · Aéreo (negro) = cobrado efectivo.
    facturada: (() => {
      const objBlanco = Number(obra?.montoCobradoNoEfectivo) || 0;
      const objNegro = Number(obra?.montoCobradoEfectivo) || 0;
      if (objBlanco <= 0.01 && objNegro <= 0.01) {
        return !!archivoDe(obra, "factura"); // compat legacy (obras viejas)
      }
      const blancoOk = objBlanco <= 0.01 || Number(obra?.montoFacturadoBlanco || 0) >= objBlanco - 0.01;
      const negroOk = objNegro <= 0.01 || Number(obra?.montoFacturadoNegro || 0) >= objNegro - 0.01;
      return blancoOk && negroOk;
    })(),
    // Cobrada = el total cobrado alcanza el monto del presupuesto (o cobro legacy)
    cobrada: (Number(obra?.montoPresupuesto) > 0 && Number(obra?.montoCobrado) >= Number(obra?.montoPresupuesto) - 0.01)
      || !!archivoDe(obra, "cobro"),
    balance: !!obra?.balanceHecho,
  };
}

/** ¿El hito está marcado por admin como cargado pero todavía sin el archivo? */
export function esHitoManual(obra, hito) {
  return !!obra?.hitosManual?.[hito];
}

/**
 * ¿La obra necesita material de Gualeguay (accesorios/telas)?
 * La señal es el pedido GUALE vinculado. Si la obra es solo R8, los pasos de
 * compra de accesorios y despacho Gualeguay no aplican.
 */
export function obraConGuale(obra) {
  return !!obra?.pedidoTelaId || !!obra?.pedidoAdjuntoGuale;
}

/**
 * Facturación anticipada: desbloqueo explícito de Judy/Andrés para facturar
 * antes de terminar de cobrar (ej. el cliente pagó el 50% y pide factura).
 * Queda registrado quién lo habilitó y por qué.
 */
export function facturacionHabilitada(obra) {
  return !!obra?.facturacionAnticipada;
}

/** ¿Se puede adjuntar/marcar este hito ya? (dependencias del proceso) */
export function hitoHabilitado(obra, hito) {
  const h = hitosObra(obra);
  const conGuale = obraConGuale(obra);
  // Todo el material despachado = R8 siempre + Guale solo si la obra lo lleva
  const todoDespachado = h.despacho && (!conGuale || h.despachoGuale);
  switch (hito) {
    case "presupuesto":     return true;
    case "op":              return true;
    case "vinculado":       return h.op && !!obra?.opConfirmado; // OP adjunto Y confirmado por Ceci
    case "facturaCompra":   return h.pedido && conGuale; // documentar la compra de accesorios
    case "recepcionCompra": return h.facturaCompra;      // (legacy) recepción, ya no requerida
    case "despacho":        return h.pedido;             // R8: perfiles al carpintero
    case "despachoGuale":   return h.pedido && conGuale; // telas de Gualeguay (los accesorios no se despachan)
    case "entregada":       return todoDespachado;       // todo el material enviado
    // Se factura recién con la obra COBRADA (el 90% de los casos). Excepción:
    // Judy/Andrés habilitan la facturación anticipada si el cliente pide factura
    // habiendo pagado solo una parte. El monto/vía se deriva de la cobranza.
    case "facturada":       return h.cobrada || facturacionHabilitada(obra);
    case "cobrada":         return todoDespachado;       // se puede cobrar sin factura previa
    case "balance":         return h.despacho;           // libre respecto de entrega/cobranza
    default:                return false;
  }
}

export function obraFinalizada(obra) {
  const h = hitosObra(obra);
  const guale = !obraConGuale(obra) || (h.facturaCompra && h.despachoGuale);
  return h.presupuesto && h.pedido && h.despacho && guale &&
    h.entregada && h.facturada && h.cobrada && h.balance;
}

// ─── Progreso y responsable (para el tablero y la ficha) ────────────────────

/**
 * Hitos visibles del progreso, en orden canónico. La COBRANZA va antes que la
 * facturación: en el 90% de las obras se factura recién con todo cobrado.
 */
export const HITOS_ORDEN = [
  "presupuesto", "pedido", "facturaCompra", "despacho", "despachoGuale",
  "entregada", "cobrada", "facturada", "balance",
];

/** Hitos que aplican a ESTA obra (sin compra/despacho Guale si es solo R8) */
export function hitosOrdenDe(obra) {
  return obraConGuale(obra)
    ? HITOS_ORDEN
    : HITOS_ORDEN.filter(k => k !== "facturaCompra" && k !== "despachoGuale");
}

export function progresoObra(obra) {
  const h = hitosObra(obra);
  const orden = hitosOrdenDe(obra);
  const completos = orden.filter(k => h[k]).length;
  return { completos, total: orden.length, hitos: h, orden };
}

// Responsable del proceso por hito (colores para el avatar)
const RESPONSABLES = {
  presupuesto:     { nombre: "Ceci",        color: "#0369a1", bg: "#e0f2fe" },
  pedido:          { nombre: "Ceci",        color: "#0369a1", bg: "#e0f2fe" },
  facturaCompra:   { nombre: "Marcela",     color: "#92400e", bg: "#fef3c7" },
  recepcionCompra: { nombre: "Marcela",     color: "#92400e", bg: "#fef3c7" },
  despacho:        { nombre: "Marcela",     color: "#92400e", bg: "#fef3c7" },
  despachoGuale:   { nombre: "Ceci",        color: "#0369a1", bg: "#e0f2fe" },
  entregada:       { nombre: "Ceci",        color: "#0369a1", bg: "#e0f2fe" },
  facturada:       { nombre: "Marcela",     color: "#92400e", bg: "#fef3c7" },
  cobrada:         { nombre: "Judy",        color: "#7e22ce", bg: "#f3e8ff" },
  balance:         { nombre: "Judy/Andrés", color: "#7e22ce", bg: "#f3e8ff" },
};

/** Quién tiene la pelota: responsable del primer hito pendiente (null si finalizada) */
export function responsableActual(obra) {
  const h = hitosObra(obra);
  const fallback = { nombre: "—", color: "#64748b", bg: "#f1f5f9" };
  for (const k of hitosOrdenDe(obra)) {
    if (!h[k]) return { hito: k, ...fallback, ...RESPONSABLES[k] };
  }
  return null;
}

// ─── Fases derivadas (para tablero, filtros y alertas) ──────────────────────

export const FASES_OBRA = [
  {
    id: "CREACION",
    label: "Creación del pedido",
    icon: "📐",
    color: "#92400e", bg: "#fffbeb", border: "#fde68a", headerBg: "#fef3c7",
    hint: "Ceci: adjuntar pedido OP y vincular el pedido de Finnegans",
  },
  {
    id: "PREPARACION_R8",
    label: "Preparación y compra",
    icon: "📦",
    color: "#0369a1", bg: "#f0f9ff", border: "#bae6fd", headerBg: "#e0f2fe",
    hint: "Marcela: compra de accesorios y despacho R8 · Ceci: despacho Gualeguay",
    diasAlerta: 5,
  },
  {
    id: "EN_CARPINTERIA",
    label: "En producción",
    icon: "🔨",
    color: "#c2410c", bg: "#fff7ed", border: "#fdba74", headerBg: "#ffedd5",
    hint: "El carpintero está armando la obra — confirmar la entrega al finalizar",
    diasAlerta: 20,
  },
  {
    id: "ENTREGADA",
    label: "Entregada · admin pendiente",
    icon: "🏠",
    color: "#7e22ce", bg: "#fdf4ff", border: "#e9d5ff", headerBg: "#f3e8ff",
    hint: "Completar factura, cobranza y balance",
    diasAlerta: 15,
  },
  {
    id: "FINALIZADA",
    label: "Finalizada",
    icon: "✅",
    color: "#15803d", bg: "#f0fdf4", border: "#86efac", headerBg: "#dcfce7",
    hint: "Obra completa: entregada, facturada, cobrada y con balance",
  },
];

export const FASES_OBRA_MAP = Object.fromEntries(FASES_OBRA.map(f => [f.id, f]));

/** Fase actual derivada de los hitos */
export function faseObra(obra) {
  const h = hitosObra(obra);
  if (obraFinalizada(obra)) return FASES_OBRA_MAP.FINALIZADA;
  if (!h.pedido) return FASES_OBRA_MAP.CREACION;
  // "En construcción" recién con TODO el material despachado (R8 + Guale si aplica)
  const todoDespachado = h.despacho && (!obraConGuale(obra) || h.despachoGuale);
  if (!todoDespachado) return FASES_OBRA_MAP.PREPARACION_R8;
  if (!h.entregada) return FASES_OBRA_MAP.EN_CARPINTERIA;
  return FASES_OBRA_MAP.ENTREGADA; // entregada pero falta factura/cobro/balance
}

// ─── Permisos y responsabilidades por usuario ───────────────────────────────
//
// Flags en users/{uid}:
//   obrasDespacho: true → Marcela (única que adjunta el despacho; también factura)
//   obrasFinanzas: true → Judy y Andrés (cobro y balance)
//   sin flags           → gestión general (Ceci: OP, vínculo, entrega)
//   admin               → puede todo, sin resaltados personales

export function permisosObras(perfil) {
  const rol = String(perfil?.rol || perfil?.role || "").toLowerCase();
  const admin = rol === "admin";
  return {
    admin,
    editar: rol === "obras" || admin,
    despacho: admin || perfil?.obrasDespacho === true,
    finanzas: admin || perfil?.obrasFinanzas === true,
  };
}

/**
 * Acciones que LE TOCAN al usuario en esta obra, ahora mismo.
 * Devuelve [{ hito, label }] — para el banner "Te toca" y los resaltados.
 */
export function accionesPendientesPara(obra, perfil) {
  const p = permisosObras(perfil);
  if (!p.editar || p.admin) return []; // admin ve todo sin resaltados
  const h = hitosObra(obra);
  const esMarcela = perfil?.obrasDespacho === true;
  const esFinanzas = perfil?.obrasFinanzas === true;
  const esGestion = !esMarcela && !esFinanzas; // Ceci

  const conGuale = obraConGuale(obra);
  const acc = [];
  if (esGestion) {
    if (!h.presupuesto) acc.push({ hito: "presupuesto", label: "Adjuntar el presupuesto de OP" });
    if (!h.op) acc.push({ hito: "op", label: "Adjuntar el pedido OP" });
    if (h.op && !obra?.opConfirmado) acc.push({ hito: "op", label: "Revisar y confirmar la lectura del OP" });
    if (h.op && obra?.opConfirmado && !h.vinculado) acc.push({ hito: "vinculado", label: "Vincular el pedido de Finnegans" });
    if (conGuale && h.pedido && !h.despachoGuale) acc.push({ hito: "despachoGuale", label: "Adjuntar el despacho de Gualeguay (telas)" });
    if (hitoHabilitado(obra, "entregada") && !h.entregada) acc.push({ hito: "entregada", label: "Marcar la obra como entregada" });
  }
  // Facturación derivada de la cobranza: no efectivo → Finnegans (Marcela) · efectivo → Aéreo (Judy)
  const facturable = hitoHabilitado(obra, "facturada");
  const objBlanco = Number(obra?.montoCobradoNoEfectivo) || 0; // Finnegans (Marcela)
  const objNegro = Number(obra?.montoCobradoEfectivo) || 0;    // Aéreo (Judy)
  const blancoCubierto = objBlanco <= 0.01 || Number(obra?.montoFacturadoBlanco || 0) >= objBlanco - 0.01;
  const negroCubierto = objNegro <= 0.01 || Number(obra?.montoFacturadoNegro || 0) >= objNegro - 0.01;

  if (esMarcela) {
    if (conGuale && h.pedido && !h.facturaCompra) acc.push({ hito: "facturaCompra", label: "Cargar la compra de accesorios (factura Finnegans o comprobantes de plaza)" });
    if (h.pedido && !h.despacho) acc.push({ hito: "despacho", label: "Adjuntar el despacho R8 (perfiles al carpintero)" });
    if (facturable && objBlanco > 0.01 && !blancoCubierto) acc.push({ hito: "facturada", label: "Facturar en Finnegans lo cobrado (no efectivo)" });
  }
  if (esFinanzas) {
    if (facturable && objNegro > 0.01 && !negroCubierto) acc.push({ hito: "facturada", label: "Cargar la factura aérea (lo cobrado en efectivo)" });
    if (hitoHabilitado(obra, "cobrada") && !h.cobrada) acc.push({ hito: "cobrada", label: "Adjuntar el documento de cobro" });
    if (h.despacho && !h.balance) acc.push({ hito: "balance", label: "Hacer el balance" });
  }
  return acc;
}

function toDate(ts) {
  if (!ts) return null;
  if (ts?.seconds) return new Date(ts.seconds * 1000);
  const d = ts instanceof Date ? ts : new Date(ts);
  return isNaN(d.getTime()) ? null : d;
}

/** Fecha en que la obra entró a su fase actual (para "hace N días" y alertas) */
export function fechaFaseActual(obra) {
  const fase = faseObra(obra);
  switch (fase.id) {
    case "CREACION":       return toDate(obra?.creadoAt);
    case "PREPARACION_R8": {
      const op = archivoDe(obra, "op");
      return toDate(op?.at) || toDate(obra?.updatedAt) || toDate(obra?.creadoAt);
    }
    case "EN_CARPINTERIA":
      return toDate(archivoDe(obra, "despachoGuale")?.at) || toDate(archivoDe(obra, "despacho")?.at) || toDate(obra?.updatedAt);
    case "ENTREGADA":      return toDate(obra?.entregada?.at) || toDate(obra?.updatedAt);
    default:               return null; // FINALIZADA: sin alertas
  }
}
